import { useCallback } from 'react';

import {
  findSelectDropdown,
  isDatePickerCalendar,
  isIgnoredElement,
  isMuiBackdrop,
  isPortalMountingIssue,
  isWithinElement,
} from '../utils';

interface UseClickAwayHandlerOptions {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

/**
 * DatePicker Calendar의 ClickAway 핸들러를 제공하는 커스텀 훅
 *
 * @description
 * 다음 경우에 Calendar를 닫지 않습니다:
 * 1. Portal 마운트 타이밍 이슈 (target이 없음)
 * 2. MUI Backdrop 클릭
 * 3. anchorEl(input) 클릭
 * 4. react-datepicker 캘린더 내부 클릭
 * 5. react-datepicker-ignore-onclickoutside 클래스 요소 클릭
 * 6. TimePicker의 Select 드롭다운 클릭
 *
 * @param options - ClickAway 핸들러 옵션
 * @returns handleClickAway 함수
 */
export const useClickAwayHandler = (options: UseClickAwayHandlerOptions) => {
  const { anchorEl, onClose } = options;

  return useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;

      // 1. Portal 마운트 타이밍 이슈
      if (isPortalMountingIssue(target)) {
        return;
      }

      // 2. MUI Backdrop 클릭 무시 (Select/DropdownMenu가 열릴 때 생성됨)
      if (isMuiBackdrop(target)) {
        return;
      }

      // 3. anchorEl(input)을 클릭한 경우는 무시
      if (isWithinElement(target, anchorEl)) {
        return;
      }

      // 4. react-datepicker 캘린더 내부를 클릭한 경우는 무시
      if (isDatePickerCalendar(target)) {
        return;
      }

      // 5. react-datepicker-ignore-onclickoutside 클래스를 가진 요소나 그 자식을 클릭한 경우 무시
      if (isIgnoredElement(target)) {
        return;
      }

      // 6. TimePicker의 Select 드롭다운(Portal)을 클릭한 경우는 무시
      if (findSelectDropdown(target)) {
        return;
      }

      onClose();
    },
    [anchorEl, onClose],
  );
};
